import React, { useEffect, useCallback } from 'react';
import QuestionPanel from "../../structure/QuestionPanel";
import { useFormikContext } from "formik";
import Checkbox from "../../input/Checkbox";
import TextInput from "../../input/TextInput";
import Radio from "../../input/Radio";
import { DisabilityEmployeeFormValues } from "../../disability/DisabilityEmployeeFormValues";
import { CriticalIllnessEmployeeFormValues } from "../../illness/CriticalIllnessEmployeeFormValues";
import { HospitalIndemnityEmployeeFormValues } from "../../indemnity/HospitalIndemnityEmployeeFormValues";
import { AccidentEmployeeFormValues } from "../../accident/AccidentEmployeeFormValues";
import { PfmlEmployeeFormValues } from "../../pfml/PfmlEmployeeFormValues";
import SelectDropdown from "../../input/SelectDropdown";
import FileInput from "../../input/FileInput";
import { DateTime } from "luxon";
import { Relationship } from "../../enums/Relationship";
import { CorrespondenceRelationship } from "../../enums/CorrespondenceRelationship";
import ShowHideDiv from "../../structure/ShowHideDiv";
import { findVirtualSupplyLinkFor } from "../../../service/LinkService";

type HipaaFormValues =
    AccidentEmployeeFormValues
    | DisabilityEmployeeFormValues
    | CriticalIllnessEmployeeFormValues
    | HospitalIndemnityEmployeeFormValues
    | PfmlEmployeeFormValues;

const EmployeeHipaa = () => {
    const { values, setFieldValue } = useFormikContext<HipaaFormValues>();

    const getAge = (dob: string): number => {
        if (!dob || dob.trim() === '') {
            return 0;
        }
        const birthDate = DateTime.fromFormat(dob, 'MM/dd/yyyy');
        if (!birthDate.isValid) {
            console.error('Invalid date format. Expected MM/dd/yyyy, received:', dob);
            return 0;
        }
        const today = DateTime.now();
        let age = today.year - birthDate.year;
        if (today.month < birthDate.month || (today.month === birthDate.month && today.day < birthDate.day)) {
            age--;
        }
        return age;
    }

    const pathForLegalGuardian = () => {
        const age = getAge(values.patientDateOfBirth);
        const isLegalGuardian = values.representativeAuthority === "Legal Guardian";
        return isLegalGuardian && age < 18;
    }

    const canFillHipaaConsent = useCallback((): boolean => {
        const auth = values.representativeAuthority;
        const age = getAge(values.patientDateOfBirth);
        const rel = values.relationship;

        if (rel === Relationship.EMPLOYEE || rel === Relationship.MYSELF) {
            return true;
        }
        if (auth === "None of the above" || age >= 18) {
            return false;
        }

        if (rel === Relationship.COVERED_DEPENDENT || rel === Relationship.FAMILY_MEMBER || rel === 'Covered dependent' || rel === 'Family member') {
            return age < 18;
        }

        return true;
    }, [values.representativeAuthority, values.patientDateOfBirth, values.relationship]);

    const showRepresentativeAuthorityDropdown = useCallback((): boolean => {
        const age = getAge(values.patientDateOfBirth);
        const rel = values.relationship;
        return rel !== Relationship.EMPLOYEE && rel !== Relationship.MYSELF && age < 18;
    }, [values.patientDateOfBirth, values.relationship]);

    useEffect(() => {
        if (!canFillHipaaConsent() && values.hipaaConsent) {
            setFieldValue('hipaaConsent', false);
        }
    }, [canFillHipaaConsent, values.hipaaConsent, setFieldValue]);


    const detailsText: string = (() => {
        if (getAge(values.patientDateOfBirth) >= 18 && !canFillHipaaConsent()){
            return "Since the dependent is over 18, you cannot consent to the HIPAA authorization on their behalf. Once the claim is submitted, you’ll receive an email with a link to share with the patient to electronically complete and sign."
        }
        else if(canFillHipaaConsent()){
            return "Please read and acknowledge the following statements regarding your HIPAA authorization.";
        }
        else if (getAge(values.patientDateOfBirth) < 18 && values.representativeAuthority === "None of the above") {
            return "Only the parent or legal guardian can consent to the HIPAA authorization on the patient's behalf. Once the claim is submitted, you’ll receive an email with a link to share with the patient's parent/legal guardian to electronically complete and sign.";
        }
        else {
            return "";
        }
    })();

    const reactHipaaPdfUrl = findVirtualSupplyLinkFor('GP49771A');

    return (
        <>
            <ShowHideDiv
                show={canFillHipaaConsent()} className='pdf-viewer'
            >
                <embed
                    src={reactHipaaPdfUrl}
                    type="application/pdf"
                    width="1320px"
                    height="1600px"
                />
            </ShowHideDiv>

            <QuestionPanel
                headerText="HIPAA Authorization"
                detailsText={detailsText}
                panelContent={
                    <div>
                        <div className="pds-u-margin-bottom-16">
                            {showRepresentativeAuthorityDropdown() && (
                                <SelectDropdown
                                    id="representativeAuthority"
                                    name="representativeAuthority"
                                    label="Your representative authority for the patient:"
                                    required={true}
                                    options={[
                                        { id: 'parent', text: 'Parent' },
                                        { id: 'legalGuardian', text: 'Legal Guardian' },
                                        { id: 'noneOfTheAbove', text: 'None of the above' }
                                    ]}
                                />
                            )}
                        </div>


                        {pathForLegalGuardian() && (
                            <>
                                <TextInput
                                    id="authorityDescription"
                                    name="authorityDescription"
                                    label="Please describe the scope of your authority to act on the patient's behalf (including a member acting as a representative on a dependent's behalf)"
                                    required={false}
                                />
                                <label htmlFor="authorityDocumentation">
                                    Please include the proper documentation that attests to your ability to
                                    sign.
                                </label>
                                {values.authorityDescription && values.authorityDescription.trim() !== '' && (
                                    <FileInput
                                        id="authorityDocumentation"
                                        name="authorityDocumentation"
                                        label="authorityDocumentation"
                                        required={false}
                                        supportedExtensions={['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx']}
                                    />
                                )}
                            </>
                        )}
                        {canFillHipaaConsent() && (
                            <div>
                                <div style={{marginBottom: '16px'}}>
                                    <div style={{marginBottom: '8px'}}>
                                        Can confidential messages be left at this
                                        number? {values.phoneNumber && `(${values.phoneNumber})`}
                                    </div>
                                    <Radio id="confidentialMessages-yes" name="confidentialMessages" text="Yes"/>
                                    <Radio id="confidentialMessages-no" name="confidentialMessages" text="No"/>
                                </div>

                                <TextInput
                                    id="permissionContactName"
                                    name="permissionContactName"
                                    label="OPTIONAL: I give you permission to speak with the following person regarding my claim"
                                    required={false}
                                />
                                <SelectDropdown
                                    id="relationshipType"
                                    label="Relationship"
                                    name="relationshipType"
                                    required={false}
                                    options={[
                                        {id: "Spouse", text: CorrespondenceRelationship.SPOUSE},
                                        {id: "Domestic Partner", text: CorrespondenceRelationship.DOMESTIC_PARTNER},
                                        {id: "Other", text: CorrespondenceRelationship.OTHER}
                                    ]}
                                />

                            </div>
                        )}

                        <ShowHideDiv show={canFillHipaaConsent()}>
                            <Checkbox
                                id="hipaa-consent"
                                label="I have read and consent to the HIPAA authorization above."
                            />
                        </ShowHideDiv>
                        {!canFillHipaaConsent() && (
                            <div style={{ color: 'red', marginTop: '16px' }}>
                                Members can only consent to the HIPAA agreement for patients under 18 years old if they are the parent or legal guardian.
                            </div>
                        )}
                    </div>
                }
            />
        </>
    );
};

export default EmployeeHipaa;